import React, { useEffect, useState, useMemo } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { Barn, Horse } from "../../models";
import Spinner from "../Spinners/Spinner";
import { IonCol } from "@ionic/react";
import ErrorAlert from "../Errors/ErrorAlert";
import VerifyHorseModal from "./VerifyHorseModal";
import { formattedOption } from "../../utilities/horse/SelectOrCreateHorseUtils";
const { v4: uuidv4 } = require("uuid");

interface SelectOrCreateHorseProps {
    userHorseOptions?: formattedOption[] | null | undefined;
    barnHorseOptions?: formattedOption[] | null | undefined;

    selectedBarn?: Barn | null | undefined;
    selectedValue?: string | null;
    onSelect: (horse?: Horse, horseName?: string) => void;
    isHeightRequired?: boolean;
    height?: string;
}

/**
 * This function is passed into the select component to define what should be shown when an option is selected
 * Instead of showing the 3 lines from the option,
 * it will only show the 1 line defined below: horseName
 * @param props
 * @returns
 */
const SingleValue = (props: any) => <components.SingleValue {...props}>{props.data.horseName || props.data.label}</components.SingleValue>;

const SelectOrCreateHorse: React.FC<SelectOrCreateHorseProps> = ({ userHorseOptions, barnHorseOptions, selectedBarn, selectedValue, onSelect, isHeightRequired, height }) => {
    // Dropdown Section Titles
    const createdByUserGroupLabel = "Horses Created by You";
    const barnHorsesGroupLabel = selectedBarn ? selectedBarn.name + " Horses" : "Barn Horses";

    // Groups - each group represents a section in the dropdown
    // Use useMemo to ensure groups are recreated when options change, forcing CreatableSelect to update
    const barnHorsesGroup = useMemo(
        () => ({
            label: barnHorsesGroupLabel,
            options:
                barnHorseOptions && barnHorseOptions.length > 0
                    ? barnHorseOptions
                    : [{ value: selectedBarn ? `No horses are in ${selectedBarn?.name}` : `No barn selected.`, label: " ", horseName: " " }],
        }),
        [barnHorseOptions, barnHorsesGroupLabel, selectedBarn]
    );

    const horsesCreatedByUserGroup = useMemo(
        () => ({
            label: createdByUserGroupLabel,
            options: userHorseOptions && userHorseOptions.length > 0 ? userHorseOptions : [{ value: "You have not created any horses yet.", label: "", horseName: "" }],
        }),
        [userHorseOptions, createdByUserGroupLabel]
    );

    // General State
    const [currentValue, setCurrentValue] = useState<formattedOption | null | undefined>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const findValueInList = (value: string) => {
        // Set up the options for arrays of horses that should be checked
        const optionArray = (userHorseOptions || [])?.concat(barnHorseOptions || []);
        // If there are options, loop through them and set the current value
        if (optionArray) {
            for (var i = 0; i < optionArray.length; i++) {
                const currentOption = optionArray[i];
                if (currentOption.value === value) {
                    setCurrentValue(currentOption);
                }
            }
        }
    };

    if (selectedValue && !currentValue) {
        findValueInList(selectedValue);
    }

    useEffect(() => {
        if (selectedValue) {
            const optionArray = (userHorseOptions || []).concat(barnHorseOptions || []);
            const match = optionArray.find((o) => o.value === selectedValue);
            if (match) {
                setCurrentValue(match);
            }
        } else {
            setCurrentValue(null);
        }
    }, [selectedValue, userHorseOptions, barnHorseOptions]);

    // DROPDOWN Functions
    const handleOnChange = async (event?: any) => {
        // Horse was selected from either the barnHorses or horsesCreatedByUser groups
        if (event && event.horse) {
            await setCurrentValue(event);
            onSelect(event.horse);
        }

        // Option from the dropdown was created by the current user with only the horse name
        else if (event && event.horseName && event.horseName !== " ") {
            const newFormattedOption = handleCreateNewFormattedOptionWithJustHorseName(event.horseNameLabel);
            await setCurrentValue(newFormattedOption);
            onSelect(undefined, event.horseName);
        }

        // Option from the dropdown was that there are no horses in this section
        else if (event && event.horseName && event.horseName === " ") {
            // Open the modal and have the user type in a name and verify the horse.
            setIsModalVisible(true);
        }

        // Here - event.value will be the value the user typed into the CreateableSelect field
        else if (event && event.value) {
            // Set the currentValue in state so that it can be used inside of the modal.
            const newFormattedOption = handleCreateNewFormattedOptionWithJustHorseName(event.value);
            await setCurrentValue(newFormattedOption);

            // Verify Horse - If the horse was not in the barnHorses or horsesCreatedByUser groups
            setIsModalVisible(true);
        }

        // User clicked the X button to clear the value.
        else {
            await setCurrentValue(null);
            onSelect(undefined);
        }
    };

    const handleCreateNewFormattedOptionWithJustHorseName = (horseName: string) => {
        const newFormattedOption: formattedOption = {
            value: uuidv4(),
            label: horseName + " " + (selectedBarn?.name || ""),
            horseName: horseName,
            horseNameLabel: horseName,
            barnLabel: selectedBarn?.name || "",
            horse: undefined,
        };
        return newFormattedOption;
    };

    /**
     * User found the horse by name in the RSP Database
     * Send that horse info back to the parent component
     * And close the modal
     */
    const handleUseMatchedHorse = async (event: formattedOption) => {
        if (event.horseName) {
            await setCurrentValue(event);
            onSelect(event.horse, event.horseName);
            setIsModalVisible(false);
        } else {
            setError("No horse name was found.");
        }
    };
    /**
     * User did not find the horse by name in the RSP Database
     * Send that horse name back to the parent component in order to create a new horse
     * And close the modal
     */
    const handleCreateNewHorse = (newHorseName: string) => {
        onSelect(undefined, newHorseName);
        setIsModalVisible(false);
    };
    /**
     * User did not find the horse by name in the RSP Database.
     * Assume the user thought the horse existed and realized there is a typo in the horse name.
     * Send the user back to the starting point to try again.
     */
    const handleCancel = () => {
        onSelect(undefined);
        setIsModalVisible(false);
        setCurrentValue(null);
    };

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <IonCol size="10">
                    {error && <ErrorAlert error={error} width="12" />}
                    <CreatableSelect
                        id="select-or-create-horse"
                        inputId="select-or-create-horse-input"
                        placeholder="Type the horse's name"
                        styles={{
                            // Fixes the overlapping problem of the component
                            control: (baseStyles) => (isHeightRequired ? { ...baseStyles, height } : { ...baseStyles }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            group: (base, state) => ({
                                ...base,
                                // Group background color
                                backgroundColor: state.label === barnHorsesGroupLabel ? "var(--ion-color-info)" : "var(--ion-color-secondary)",
                                padding: "10px",
                            }),
                            groupHeading: (base, state) => ({
                                ...base,
                                fontSize: "1em",
                                fontWeight: "bold",
                                // Section title color
                                color: "var(--ion-color-white)",
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                                ...base,
                                // Hover vs. no hover colors for horses in list
                                backgroundColor: isFocused || isSelected ? "var(--ion-color-light)" : "var(--ion-color-white)",
                                cursor: "pointer",
                                color: "var(--ion-color-primary)",
                            }),
                        }}
                        defaultValue={currentValue}
                        value={currentValue}
                        menuPortalTarget={document.body}
                        menuPlacement={"auto"}
                        isClearable
                        options={useMemo(() => [barnHorsesGroup, horsesCreatedByUserGroup], [barnHorsesGroup, horsesCreatedByUserGroup])}
                        noOptionsMessage={({ inputValue }) => (!inputValue ? "No input found" : "No results found")}
                        formatOptionLabel={(option: any) =>
                            option.horseNameLabel ? (
                                <div className="px-10">
                                    <div className="font-weight-bold text-default">{option.horseNameLabel}</div>
                                    {barnHorsesGroup.options.includes(option) ? (
                                        <div className="display-5 text-medium">Created By: {option.createdByLabel}</div>
                                    ) : (
                                        <div className="display-5 text-medium">Barn: {option.barnLabel}</div>
                                    )}
                                    <div className="display-5 text-medium">Owner: {option.ownerLabel}</div>
                                </div>
                            ) : (
                                <div className="p-0">
                                    <div className="font-weight-bold text-default">{option.textToDisplay || "Select or Create Horse: " + option.value}</div>
                                </div>
                            )
                        }
                        components={{ SingleValue }}
                        onChange={(event: any) => handleOnChange(event)}
                        createOptionPosition={"first"}
                    />
                </IonCol>
            )}
            {isModalVisible && (
                <VerifyHorseModal
                    currentValue={currentValue}
                    handleCancel={() => handleCancel()}
                    handleUseMatchedHorse={(e: formattedOption) => handleUseMatchedHorse(e)}
                    handleCreateNewHorse={(newHorseName: string) => handleCreateNewHorse(newHorseName)}
                />
            )}
        </>
    );
};

export default SelectOrCreateHorse;
